import { useState, useRef, useCallback, useDeferredValue } from 'react';
import PropTypes from 'prop-types';
import { cn } from 'Utils/cn';
import { INVOICE_VALID_FORMATS } from 'Utils/constants';
import { useTranslation } from 'react-i18next';
import Loading from 'Components/Atoms/Loading/Loading';

function UploadIcon({ color = 'currentColor' }) {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="31" height="38" viewBox="0 0 31 38">
            <g fill={color} fillRule="nonzero" transform="translate(-763 -451)">
                <g transform="translate(763 451)">
                    <path d="M29.315 6.238L23.95 1.393A5.265 5.265 0 0020.353 0H5.3C2.382 0 0 2.327 0 5.178v27.644C0 35.672 2.382 38 5.299 38h20.402C28.618 38 31 35.672 31 32.822V10.038c0-1.44-.616-2.818-1.685-3.8zm-19.106 6.777c4.381-4.28 4.229-4.153 4.392-4.26.259-.173.583-.268.89-.268h.017c.421 0 .827.174 1.134.459 1.69 1.656 4.319 4.236 4.149 4.069a1.552 1.552 0 010 2.233 1.64 1.64 0 01-2.285 0l-1.377-1.346v5.589c0 .886-.73 1.583-1.62 1.583-.908 0-1.621-.697-1.621-1.583V13.9l-1.394 1.362c-.606.593-1.64.63-2.285 0a1.57 1.57 0 010-2.248zm11.57 16.498H9.221c-.892 0-1.62-.712-1.62-1.583 0-.87.728-1.583 1.62-1.583h12.558c.892 0 1.62.712 1.62 1.583 0 .87-.728 1.583-1.62 1.583z"></path>
                </g>
            </g>
        </svg>
    );
}

UploadIcon.propTypes = {
    color: PropTypes.string,
};

const acceptedFormats = INVOICE_VALID_FORMATS.join(',');

export default function InvoiceEditorDrawerPreviewUpload(props) {
    const { onUpload, onRemove, className, compactMode } = props;
    const [dragging, setDragging] = useState(false);
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const isLoading = useDeferredValue(uploading);

    const timer = useRef(null);

    const { t } = useTranslation('invoices');

    const debounceDragLeave = useCallback(() => {
        timer.current = setTimeout(() => {
            setDragging(false);
        }, 100);
    }, []);

    const dragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();

        clearTimeout(timer.current);
        setDragging(true);
    }, []);

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        setDragging(false);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const file = e.dataTransfer.files[0];
            setFile(file);
            setUploading(true);
            await onUpload?.(file);
            setUploading(false);
        }
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];

        if (file) {
            setFile(file);
            setUploading(true);
            await onUpload?.(file);
            setUploading(false);
        }
    };
    const handleRemoveFile = () => {
        setFile(null);
        onRemove?.(null);
    };

    if (isLoading) {
        return (
            <div className="bg-stone-50 flex items-center justify-center h-full p-4">
                <Loading />
            </div>
        );
    }

    if (file) {
        return (
            <div className={cn('bg-stone-50 flex items-center justify-center h-full p-4', className)}>
                <div className="relative bg-white rounded-[20px] w-full max-w-[344px] mx-auto py-10 px-8 flex-grow shadow-sm text-gray-900 text-center">
                    <div className="mb-4 flex justify-center">
                        <UploadIcon color="#4021C8" />
                    </div>
                    <div className="font-semibold text-base leading-tight mb-2 lg:mb-6">{file.name}</div>
                    <button type="button" className="text-xs text-primary" onClick={handleRemoveFile}>
                        {t('press-here-to-remove-file')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div
            className={cn(
                'bg-neutral-100 flex items-center justify-center h-full p-4',
                dragging && 'bg-[#d7d1f8]',
                compactMode && 'p-0 bg-transparent',
                className,
            )}
            onDragEnter={dragEnter}
            onDragLeave={debounceDragLeave}
            onDragOver={dragEnter}
            onDrop={handleDrop}
        >
            <label
                className={cn(
                    'relative bg-white rounded-[18px] w-full max-w-[304px] mx-auto border-1 border-dashed border-gray-300 py-14 px-16 flex-grow',
                    dragging && 'text-primary border-primary',
                    compactMode && 'max-w-full w-full p-4',
                )}
            >
                <div className={cn('text-center text-gray-500 z-[1] relative', dragging && 'opacity-0')}>
                    <div className="mb-4 flex justify-center">
                        <UploadIcon />
                    </div>
                    <div className="font-semibold text-base mb-1">{t('upload-invoice-title')}</div>
                    <div className="text-xs">{t('upload-invoice-description')}</div>
                </div>
                <input
                    type="file"
                    name="files[]"
                    className="absolute top-0 left-0 opacity-0 w-full h-full cursor-pointer"
                    accept={acceptedFormats}
                    onChange={handleFileChange}
                />

                {dragging && (
                    <div className="absolute top-0 left-0 w-full h-full rounded-[inherit] flex items-center justify-center flex-col gap-4 text-sm font-semibold px-6 text-center">
                        <UploadIcon />
                        <div className="text-center">{t('drop-files-here')}</div>
                    </div>
                )}
            </label>
        </div>
    );
}

InvoiceEditorDrawerPreviewUpload.propTypes = {
    onUpload: PropTypes.func.isRequired,
    onRemove: PropTypes.func,
    className: PropTypes.string,
    compactMode: PropTypes.bool,
};
